import { useLayoutEffect, useEffect, useRef, useState } from 'react';
import { Parallax } from 'react-parallax';
import { gsap } from 'gsap';

import Header from 'components/Header';
import HeaderNav from 'components/HeaderNav';
import ProfileSection from 'features/bio/components/ProfileSection';
import ExperienceSection from 'features/bio/components/ExperienceSection';
import AbilitiesSection from 'features/bio/components/AbilitiesSection';
import ProjectsSection from 'features/bio/components/ProjectsSection';
import ContactSection from 'features/bio/components/ContactSection';
import PulsingButton from 'components/PulsingButton';
import ParticlesAttract from 'components/ParticlesAttract';
import HeroCTA_v2 from 'components/HeroCTA_v2';
import ParticlesAttract_v2 from 'components/ParticlesAttract_v2';
import ABToggle from 'components/ABToggle';

import styles from './bioview.module.scss';
import Img2 from 'assets/images/waves-rock.jpg';
import Img3 from 'assets/images/math.jpg';
import Img4 from 'assets/images/black_tile.jpg';
import Img5 from 'assets/images/waves-2.png';
import Img6 from 'assets/images/black-cubes.jpg';

const BioView: React.FC = () => {
  const [heroVersion, setHeroVersion] = useState<'v1' | 'v2'>('v2');
  const [nameComplete, setNameComplete] = useState(false);
  const [sequenceKey, setSequenceKey]   = useState(0);
  const [ctaReady, setCtaReady]         = useState(false);
  const [isAtTop, setIsAtTop]           = useState(true);
  const wasAtTopRef                     = useRef(true);

  // Scroll listener — restart CTA sequence on scroll-back-to-top
  // nameComplete is NOT reset (name and menu remain visible)
  useEffect(() => {
    const onScroll = () => {
      const atTop = window.scrollY < 80;
      const wasAtTop = wasAtTopRef.current;
      wasAtTopRef.current = atTop;
      setIsAtTop(atTop);                      // must fire first — prop is set before remount
      if (atTop && !wasAtTop) {
        setCtaReady(false);
        setSequenceKey(k => k + 1);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Set parallax bg initially hidden
  useLayoutEffect(() => {
    gsap.set('.parallaxHomeRoot', { opacity: 0 });
  }, []);

  // When name completes → fade in parallax bg (simultaneously with menu + CTA start)
  useEffect(() => {
    if (!nameComplete) return;
    gsap.to('.parallaxHomeRoot', { opacity: 1, duration: 1.2, ease: 'power1.inOut' });
  }, [nameComplete]);

  return (
    <div className={styles.bioRoot}>
      <Header onNameComplete={() => setNameComplete(true)} />
      <HeaderNav shouldAnimate={nameComplete} />
      <Parallax
        className="parallaxHomeRoot"
        blur={0}
        bgImage={Img6}
        bgImageAlt="wave"
        strength={600}
      >
        <div className={styles.parallaxHomeRoot}>
          <div className={`${styles.hero} hero`}>
            {heroVersion === 'v1' && <ParticlesAttract />}
            {heroVersion === 'v1' && <PulsingButton />}
          </div>
          <div className={styles.parallaxNavHeader}>profile</div>
        </div>
      </Parallax>
      <ProfileSection />
      <Parallax blur={{ min: -1, max: 3 }} bgImage={Img5} bgImageAlt="wave" strength={100}>
        <div className={styles.parallaxSmallRoot}>
          <div className={styles.parallaxNavHeader}>experiences</div>
        </div>
      </Parallax>
      <ExperienceSection />
      <Parallax blur={{ min: -1, max: 2 }} bgImage={Img3} bgImageAlt="wave" strength={300}>
        <div className={styles.parallaxSmallRoot}>
          <div className={styles.parallaxNavHeader}>abilities</div>
        </div>
      </Parallax>
      <AbilitiesSection />
      <Parallax blur={{ min: -1, max: 2 }} bgImage={Img4} bgImageAlt="wave" strength={100}>
        <div className={styles.parallaxSmallRoot}>
          <div className={styles.parallaxNavHeader}>projects</div>
        </div>
      </Parallax>
      <ProjectsSection />
      <Parallax blur={{ min: -1, max: 3 }} bgImage={Img2} bgImageAlt="wave" strength={100}>
        <div className={styles.parallaxSmallRoot}>
          <div className={styles.parallaxNavHeader}>contact</div>
        </div>
      </Parallax>
      <ContactSection />

      {heroVersion === 'v2' && (
        <HeroCTA_v2
          key={sequenceKey}
          isAtTop={isAtTop}
          shouldStart={nameComplete}
          onCTAReady={() => setCtaReady(true)}
        />
      )}
      {heroVersion === 'v2' && ctaReady && isAtTop && (
        <ParticlesAttract_v2 key={sequenceKey} />
      )}
      <ABToggle version={heroVersion} onToggle={() => setHeroVersion(v => v === 'v1' ? 'v2' : 'v1')} />
    </div>
  );
};

export default BioView;
